import { useEffect, useRef, useCallback } from 'react';
import { devContainerAPI } from '@/lib/dev-containers/api';

// Heartbeat interval: configurable via VITE_DEV_CONTAINER_HEARTBEAT_INTERVAL_MS, defaults to 5 minutes
const HEARTBEAT_INTERVAL_MS = parseInt(
  import.meta.env.VITE_DEV_CONTAINER_HEARTBEAT_INTERVAL_MS || '300000',
  10,
);

// Threshold for detecting long absence (e.g., laptop sleep)
// Configurable via VITE_DEV_CONTAINER_WAKE_CHECK_THRESHOLD_MS, defaults to 30 min
const LONG_ABSENCE_THRESHOLD = parseInt(
  import.meta.env.VITE_DEV_CONTAINER_WAKE_CHECK_THRESHOLD_MS || '1800000',
  10,
);

interface UseDevContainerHeartbeatOptions {
  devContainerId: string | undefined;
  onContainerPaused?: () => void;
  /**
   * Fired when the backend reports the workspace deployment is not ready
   * while DB status is still "running" (e.g. node failure). May be a
   * transient blip; the handler is responsible for debouncing before
   * showing recovery UI.
   *
   * `isInitial` is true for the mount-time check: a fresh mount has no
   * established session to flap-protect, so the handler can (and should)
   * skip its debounce and show recovery UI immediately — otherwise a user
   * who refreshes or navigates back into a dead workspace sees a
   * normal-looking zombie editor for up to a minute (hit live 2026-08-05).
   */
  onContainerUnhealthy?: (isInitial?: boolean) => void;
}

/**
 * Hook to send periodic heartbeats to keep dev container active
 * Only sends heartbeats when:
 * - devContainerId is provided
 * - Tab is visible (not hidden)
 *
 * Also detects when browser wakes from sleep and container may have been paused.
 * If absent > 30min, checks container status and triggers onContainerPaused callback.
 */
export function useDevContainerHeartbeat({
  devContainerId,
  onContainerPaused,
  onContainerUnhealthy,
}: UseDevContainerHeartbeatOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastHeartbeatRef = useRef<number>(0);

  const sendHeartbeat = useCallback(async (isInitial: boolean = false) => {
    if (!devContainerId) return;

    // Only send if tab is visible
    if (document.visibilityState !== 'visible') return;

    try {
      const res = await devContainerAPI.heartbeat(devContainerId);
      lastHeartbeatRef.current = Date.now();

      // Container was paused out-of-band (manual, maintenance, crash recovery).
      // Trigger the same resume flow Path 2 uses so we don't sit in a zombie UI.
      if (res?.status === 'paused') {
        console.debug('[Heartbeat] Heartbeat detected paused container, triggering resume');
        onContainerPaused?.();
        return;
      }

      // DB says running but the K8s deployment is not ready (node failure or
      // pod crash): everything in the editor is dead while the UI looks normal.
      if (res?.status === 'running' && res?.healthy === false) {
        console.debug('[Heartbeat] Heartbeat reports unhealthy running container');
        onContainerUnhealthy?.(isInitial);
      }
    } catch (error) {
      // Silently fail - heartbeat is best-effort
      console.debug('[Heartbeat] Failed to send heartbeat:', error);
    }
  }, [devContainerId, onContainerPaused, onContainerUnhealthy]);

  useEffect(() => {
    if (!devContainerId) return;

    // Initialize the timestamp for visibility change detection
    lastHeartbeatRef.current = Date.now();

    // Mount-time health check (the "zombie editor" fix, 2026-08-05): a page
    // refresh or route-nav back into a DEAD workspace used to render a
    // normal-looking editor until the first interval heartbeat. Check once
    // shortly after mount — short delay so a resume-in-progress can settle —
    // and flag it isInitial so the handler skips its anti-flap debounce.
    // (lastAccessedAt was already updated by access-url; one extra POST is
    // harmless.)
    const initialCheck = setTimeout(() => sendHeartbeat(true), 3000);

    // Set up interval for periodic heartbeats
    intervalRef.current = setInterval(() => sendHeartbeat(), HEARTBEAT_INTERVAL_MS);

    // Handle visibility change - check container status and send heartbeat when tab becomes visible
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;

      // If we've been "asleep" for a long time, container might be paused
      if (timeSinceLastHeartbeat >= LONG_ABSENCE_THRESHOLD && devContainerId) {
        try {
          const data = await devContainerAPI.getDevContainer(devContainerId);
          if (data.status === 'paused') {
            console.debug('[Heartbeat] Container paused during sleep, triggering resume');
            onContainerPaused?.();
            return; // Don't send heartbeat, let resume flow handle it
          }
        } catch (error) {
          console.debug('[Heartbeat] Failed to check container status:', error);
        }
      }

      // Container still running - send heartbeat as normal
      if (timeSinceLastHeartbeat >= HEARTBEAT_INTERVAL_MS) {
        sendHeartbeat();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialCheck);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [devContainerId, sendHeartbeat, onContainerPaused]);
}
